/**
 * WiFiRolloutWizard — guided onboarding for a freshly-configured Wi-Fi
 * provider. Walks the operator through 5 steps and pinpoints which one
 * fails — invaluable when bringing a Zebra NX-5500 + FreeRADIUS online.
 *
 * Steps (matches backend /api/wifi/provider/wizard/run)
 *   1. health      Probe the provider host (RADIUS Access-Request / SSH version / etc.)
 *   2. voucher     Generate a disposable test voucher tagged "wizard"
 *   3. activate    Call provider.activate_session with a synthetic MAC
 *   4. disconnect  Call provider.disconnect_session (RFC 5176 CoA / WiNG CLI)
 *   5. cleanup     Delete the test voucher + session row
 *
 * Opaque `state` is passed back to the server on every call so subsequent
 * steps know which voucher to use / clean up.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Heart, Ticket, Play, PowerOff, Eraser, CheckCircle2, AlertCircle,
  Loader2, ChevronRight, RotateCcw,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

const STEPS = [
  { key: 'health',     label: 'Reach provider',     icon: Heart,     blurb: 'Probe the RADIUS / SSH host.' },
  { key: 'voucher',    label: 'Mint test voucher',  icon: Ticket,    blurb: 'Disposable code tagged "wizard".' },
  { key: 'activate',   label: 'Activate session',   icon: Play,      blurb: 'Synthetic MAC opens a session.' },
  { key: 'disconnect', label: 'Disconnect (CoA)',   icon: PowerOff,  blurb: 'RFC 5176 / WiNG kick.' },
  { key: 'cleanup',    label: 'Clean up',           icon: Eraser,    blurb: 'Delete voucher + session row.' },
];

const StepBadge = ({ status }) => {
  if (status === 'running') return <Loader2 className="h-4 w-4 animate-spin text-sky-400" />;
  if (status === 'pass')    return <CheckCircle2 className="h-5 w-5 text-emerald-400" />;
  if (status === 'fail')    return <AlertCircle className="h-5 w-5 text-rose-400" />;
  return <div className="h-2 w-2 rounded-full bg-slate-600" />;
};

export const WiFiRolloutWizard = ({ tenantId = '' }) => {
  const [results, setResults] = useState({});  // {stepKey: {ok, message, details}}
  const [state,   setState]   = useState({});  // server-side opaque state
  const [running, setRunning] = useState(null); // current running step key

  const reset = () => { setResults({}); setState({}); setRunning(null); };

  const run = async (stepKey) => {
    setRunning(stepKey);
    try {
      const res = await axios.post(`${API}/api/wifi/provider/wizard/run`,
        { step: stepKey, tenant_id: tenantId || null, state },
        { headers: authH() });
      setResults(prev => ({ ...prev, [stepKey]: res.data }));
      if (res.data.state) setState(res.data.state);
      if (res.data.ok) {
        toast.success(`${stepKey}: ${res.data.message}`);
      } else {
        toast.error(`${stepKey} failed — see details`);
      }
    } catch (e) {
      setResults(prev => ({ ...prev, [stepKey]: { ok: false, step: stepKey,
        message: e?.response?.data?.detail || e.message } }));
      toast.error(`${stepKey} crashed`);
    } finally {
      setRunning(null);
    }
  };

  const runAll = async () => {
    reset();
    let curState = {};
    for (const s of STEPS) {
      setRunning(s.key);
      try {
        const res = await axios.post(`${API}/api/wifi/provider/wizard/run`,
          { step: s.key, tenant_id: tenantId || null, state: curState },
          { headers: authH() });
        setResults(prev => ({ ...prev, [s.key]: res.data }));
        if (res.data.state) { curState = res.data.state; setState(curState); }
        if (!res.data.ok) {
          toast.error(`Stopped at "${s.label}" — ${res.data.message}`);
          break;
        }
      } catch (e) {
        setResults(prev => ({ ...prev, [s.key]: { ok: false, step: s.key, message: e.message } }));
        break;
      }
    }
    setRunning(null);
  };

  const passCount = Object.values(results).filter(r => r.ok).length;
  const failCount = Object.values(results).filter(r => r.ok === false).length;
  const allDone   = passCount + failCount === STEPS.length;

  return (
    <Card className="bg-slate-900/60 border-slate-800" data-testid="wifi-rollout-wizard">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base text-white inline-flex items-center gap-2">
              <ChevronRight className="h-4 w-4 text-indigo-400" />
              Roll-out wizard
              {tenantId && <Badge variant="outline" className="ml-2 text-slate-300 text-[10px]">tenant {tenantId.slice(-6)}</Badge>}
            </CardTitle>
            <p className="text-xs text-slate-400 mt-1">
              Verifies that the provider you just saved can mint a voucher, activate a
              session and disconnect it — exactly the round-trip a real captive portal
              user makes. Safe to run repeatedly; cleanup step removes the test voucher.
            </p>
          </div>
          <div className="flex gap-1.5 shrink-0">
            <Button size="sm" variant="outline" onClick={reset}
                    className="border-slate-700 text-slate-200" disabled={!!running}>
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
            <Button size="sm" onClick={runAll} disabled={!!running} data-testid="wifi-wizard-run-all">
              {running ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />}
              Run all
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {STEPS.map((s, i) => {
          const r = results[s.key];
          const status = running === s.key ? 'running' : r?.ok ? 'pass' : (r?.ok === false ? 'fail' : 'idle');
          const Icon = s.icon;
          return (
            <div key={s.key} className={`border rounded p-3 transition ${
                status === 'pass' ? 'border-emerald-500/30 bg-emerald-500/5' :
                status === 'fail' ? 'border-rose-500/30 bg-rose-500/5' :
                status === 'running' ? 'border-sky-500/30 bg-sky-500/5' :
                'border-slate-800 bg-slate-900/40'
              }`} data-testid={`wifi-wizard-step-${s.key}`}>
              <div className="flex items-center gap-3">
                <div className="w-6 flex justify-center"><StepBadge status={status} /></div>
                <Icon className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium">
                    {i + 1}. {s.label}
                  </div>
                  <div className="text-xs text-slate-500">{s.blurb}</div>
                </div>
                <Button size="sm" variant="outline" className="border-slate-700 text-slate-200"
                          onClick={() => run(s.key)} disabled={!!running}
                          data-testid={`wifi-wizard-run-${s.key}`}>
                  {running === s.key ? 'Running…' : (r ? 'Re-run' : 'Run')}
                </Button>
              </div>
              {r && (
                <div className={`mt-2 ml-9 text-xs ${r.ok ? 'text-emerald-300' : 'text-rose-300'}`}>
                  {r.message}
                  {r.hint && <div className="text-amber-300 mt-1">💡 {r.hint}</div>}
                  {r.voucher_code && <div className="text-slate-400 mt-1">Voucher: <code className="text-indigo-300">{r.voucher_code}</code></div>}
                  {r.details && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-slate-500">raw response</summary>
                      <pre className="bg-slate-950/80 border border-slate-800 rounded p-2 mt-1 text-[10px] text-slate-300 overflow-x-auto">{JSON.stringify(r.details, null, 2)}</pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {allDone && (
          <div className={`mt-3 p-3 rounded border ${
            failCount === 0
              ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
              : 'border-amber-500/40 bg-amber-500/10 text-amber-200'
          }`} data-testid="wifi-wizard-summary">
            {failCount === 0
              ? `All ${passCount} checks passed — provider is production-ready.`
              : `${failCount} step(s) failed — fix the issues above and Re-run.`}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WiFiRolloutWizard;
